import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import ChangePasswordModal from '../components/ChangePasswordModal'

const EDUCATION_LEVELS = ['10th', '12th', 'Degree', 'Post Graduation']
const GENDER_OPTIONS   = ['Male', 'Female', 'Prefer not to say', 'Other']

const KERALA_DISTRICTS = [
  'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod', 'Kollam',
  'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad', 'Pathanamthitta',
  'Thiruvananthapuram', 'Thrissur', 'Wayanad',
]

const EDU_DETAIL_LABEL = {
  '10th':            'Board / Certificate (e.g. SSLC, THSLC)',
  '12th':            'Stream (e.g. Science, Commerce, Arts)',
  'Degree':          'Degree name & field (e.g. B.Sc Computer Science)',
  'Post Graduation': 'PG degree & field (e.g. M.A. Social Work)',
}

const REQUIRED_FIELDS = [
  'full_name', 'age', 'gender', 'phone',
  'address_line1', 'city', 'district', 'pin_code',
  'education_level', 'education_detail',
]

function isFormValid(profile) {
  return REQUIRED_FIELDS.every(f => String(profile[f] || '').trim() !== '')
}

function fieldError(field, value, touched) {
  if (!touched[field]) return ''
  if (String(value || '').trim() === '') return 'This field is required'
  if (field === 'age' && (Number(value) < 1 || Number(value) > 120)) return 'Enter a valid age between 1–120'
  if (field === 'pin_code' && !/^\d{6}$/.test(value)) return 'Must be a 6-digit PIN code'
  if (field === 'phone' && !/^[6-9]\d{9}$/.test(String(value).replace(/\s/g, ''))) return 'Enter a valid 10-digit Indian mobile number'
  return ''
}

// Field must live outside ProfileForm — defining it inside causes React to treat
// it as a new component type on every render, unmounting the input and losing focus.
function Field({ field, label, error, onBlur, children }) {
  return (
    <div className="form-group">
      <label>{label} <span style={{ color: 'var(--red)' }}>*</span></label>
      {React.cloneElement(children, {
        onBlur: () => onBlur(field),
        style: error ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.1)' } : {},
      })}
      {error && <span className="field-error">⚠ {error}</span>}
    </div>
  )
}

// ── Main Profile Form ─────────────────────────────────────────────────────────
export default function ProfileForm({ session }) {
  const [profile, setProfile] = useState({
    full_name: '', age: '', gender: '',
    education_level: '', education_detail: '',
    address_line1: '', city: '', district: '', state: 'Kerala', pin_code: '',
    phone: '', email: session.user.email,
  })
  const [touched, setTouched]                 = useState({})
  const [loading, setLoading]                 = useState(true)
  const [fetchFailed, setFetchFailed]         = useState('')
  const [saving, setSaving]                   = useState(false)
  const [error, setError]                     = useState('')
  const [saved, setSaved]                     = useState(false)
  const [isNew, setIsNew]                     = useState(false)
  const [submitAttempted, setSubmitAttempted]       = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm]   = useState(false)
  const [showTopMenu, setShowTopMenu]               = useState(false)
  const [deleteText, setDeleteText]                 = useState('')
  const [deleteLoading, setDeleteLoading]           = useState(false)

  const loadProfile = async () => {
    setLoading(true)
    setFetchFailed(false)
    let done = false
    const timeout = setTimeout(() => {
      if (!done) { setFetchFailed('Could not connect to server.'); setLoading(false) }
    }, 8000)

    try {
      const { data, error } = await supabase
        .from('profiles').select('*')
        .eq('id', session.user.id).single()
      done = true
      clearTimeout(timeout)
      if (data && !error) {
        if (data.is_admin) { window.location.replace('/dashboard'); return }
        setProfile({ ...data, email: session.user.email, state: data.state || 'Kerala' })
        const isEmpty = !data.full_name
        setIsNew(isEmpty)
        if (!isEmpty) {
          const allTouched = {}
          REQUIRED_FIELDS.forEach(f => allTouched[f] = true)
          setTouched(allTouched)
        }
      } else if (error?.code === 'PGRST116') {
        setIsNew(true)
      } else {
        setFetchFailed(error?.message || 'Failed to load profile.')
      }
    } catch (e) {
      done = true
      clearTimeout(timeout)
      setFetchFailed(e?.message || 'Could not connect to server.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadProfile() }, [session])

  const handleChange = (field, value) => {
    setSaved(false)
    if (field === 'education_level') {
      setProfile(p => ({ ...p, education_level: value, education_detail: '' }))
      setTouched(t => ({ ...t, education_level: true, education_detail: false }))
    } else {
      setProfile(p => ({ ...p, [field]: value }))
    }
  }

  const handleBlur = (field) => setTouched(t => ({ ...t, [field]: true }))

  const handleSave = async (e) => {
    e.preventDefault()
    setSubmitAttempted(true)
    const allTouched = {}
    REQUIRED_FIELDS.forEach(f => allTouched[f] = true)
    setTouched(allTouched)

    if (!isFormValid(profile)) {
      setError('Please fill in all required fields before saving.')
      return
    }

    const formatError = REQUIRED_FIELDS.map(f => fieldError(f, profile[f], allTouched)).find(e => e !== '')
    if (formatError) {
      setError(formatError)
      return
    }
    setError('')
    setSaving(true)

    const { error } = await supabase.from('profiles').upsert({
      id:               session.user.id,
      full_name:        profile.full_name.trim(),
      age:              parseInt(profile.age) || null,
      gender:           profile.gender,
      education_level:  profile.education_level,
      education_detail: profile.education_detail.trim(),
      address_line1:    profile.address_line1.trim(),
      city:             profile.city.trim(),
      district:         profile.district.trim(),
      state:            'Kerala',
      pin_code:         profile.pin_code.trim(),
      phone:            profile.phone.trim(),
      email:            session.user.email,
    }, { onConflict: 'id' })

    if (error) { setError(error.message) }
    else { setSaved(true); setIsNew(false); setError('') }
    setSaving(false)
  }

  const fe = (field) => fieldError(field, profile[field], touched)

  const valid        = isFormValid(profile)
  const filledCount  = REQUIRED_FIELDS.filter(f => String(profile[f] || '').trim() !== '').length
  const progress     = Math.round((filledCount / REQUIRED_FIELDS.length) * 100)
  const progressColor = valid ? 'var(--green)' : 'var(--uplift)'

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <p>Loading your profile…</p>
    </div>
  )

  if (fetchFailed) return (
    <div className="loading-screen">
      <p style={{ color: 'var(--red)', marginBottom: 16 }}>⚠ {fetchFailed}</p>
      <button className="btn btn-primary" style={{ width: 'auto', padding: '10px 28px' }}
        onClick={loadProfile}>
        Try again
      </button>
      <button className="btn btn-ghost" style={{ marginTop: 10, width: 'auto', padding: '9px 20px', fontSize: '0.85rem' }}
        onClick={() => { localStorage.clear(); window.location.replace('/login') }}>
        Sign out
      </button>
    </div>
  )

  return (
    <div className="page-layout">
      <div className="topbar">
        <div className="topbar-brand">
          <div className="topbar-logo-mark">↑</div>
          <span className="topbar-logo-name">Uplift</span>
        </div>
        <div className="topbar-actions">
          <span className="topbar-email">{session.user.email}</span>
          <button className="btn btn-ghost topbar-hide-mobile" onClick={() => setShowChangePassword(true)}
            style={{ width: 'auto', padding: '7px 16px', fontSize: '0.85rem' }}>
            🔑 Change password
          </button>
          <button className="btn btn-ghost topbar-hide-mobile" onClick={() => setShowDeleteConfirm(true)}
            style={{ width: 'auto', padding: '7px 16px', fontSize: '0.85rem', color: 'var(--red)' }}>
            🗑 Delete account
          </button>
          <button className="topbar-menu-btn" onClick={() => setShowTopMenu(v => !v)}>⋮</button>
          <button className="btn btn-danger" onClick={() => { supabase.auth.signOut(); localStorage.clear(); window.location.replace('/login') }}>Sign out</button>
          {showTopMenu && (
            <div className="topbar-dropdown">
              <button onClick={() => { setShowTopMenu(false); setShowChangePassword(true) }}>🔑 Change password</button>
              <button onClick={() => { setShowTopMenu(false); setShowDeleteConfirm(true) }} style={{ color: 'var(--red)' }}>🗑 Delete account</button>
            </div>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="page-header">
          <h1>{isNew ? 'Complete your profile' : 'Your profile'}</h1>
          <p>{isNew
            ? 'All fields are required. Fill in your details and click Submit.'
            : 'Keep your details up to date. All fields are required.'
          }</p>
        </div>

        {/* Progress */}
        {!saved && (
          <div className="progress-wrap">
            <div className="progress-meta">
              <span>Profile completion</span>
              <span style={{ color: progressColor }}>{filledCount} / {REQUIRED_FIELDS.length} fields filled</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${progress}%`, background: progressColor }} />
            </div>
          </div>
        )}

        <div className="form-card">
          {error && <div className="alert alert-error">⚠ {error}</div>}
          {saved  && <div className="alert alert-success">✓ Your profile has been saved successfully.</div>}

          <form onSubmit={handleSave} noValidate>

            {/* Personal */}
            <div className="section-label">👤 Personal Information</div>

            <Field field="full_name" label="Full name" error={fe('full_name')} onBlur={handleBlur}>
              <input type="text" value={profile.full_name}
                onChange={e => handleChange('full_name', e.target.value)}
                placeholder="Enter your full name" />
            </Field>

            <div className="form-row">
              <Field field="age" label="Age" error={fe('age')} onBlur={handleBlur}>
                <input type="number" min="1" max="120" value={profile.age}
                  onChange={e => handleChange('age', e.target.value)}
                  placeholder="e.g. 28" />
              </Field>
              <Field field="gender" label="Gender" error={fe('gender')} onBlur={handleBlur}>
                <select value={profile.gender}
                  onChange={e => handleChange('gender', e.target.value)}>
                  <option value="">Select…</option>
                  {GENDER_OPTIONS.map(g => <option key={g}>{g}</option>)}
                </select>
              </Field>
            </div>

            {/* Contact */}
            <div className="section-label">📞 Contact Details</div>

            <div className="form-group">
              <label>Email address <span style={{ fontSize: '0.75rem', color: 'var(--ink-soft)', textTransform: 'none', fontWeight: 400 }}>(from your account)</span></label>
              <input type="email" readOnly value={profile.email} tabIndex="-1" />
            </div>

            <div className="form-group">
              <label>Phone number <span style={{ color: 'var(--red)' }}>*</span></label>
              <div style={{
                display: 'flex', alignItems: 'stretch',
                border: `1.5px solid ${fe('phone') ? 'var(--red)' : 'var(--border)'}`,
                borderRadius: 8, overflow: 'hidden',
                boxShadow: fe('phone') ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none',
              }}>
                <span style={{
                  padding: '0 12px', background: 'var(--uplift-pale2)',
                  borderRight: `1.5px solid var(--border)`,
                  display: 'flex', alignItems: 'center',
                  fontSize: '0.9rem', color: 'var(--ink-soft)', fontWeight: 600,
                }}>+91</span>
                <input type="tel" value={profile.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  onBlur={() => handleBlur('phone')}
                  placeholder="10-digit mobile number"
                  style={{ border: 'none', borderRadius: 0, flex: 1, outline: 'none' }} />
              </div>
              {fe('phone') && <span className="field-error">⚠ {fe('phone')}</span>}
            </div>

            {/* Address */}
            <div className="section-label">📍 Address</div>

            <Field field="address_line1" label="Address Line 1" error={fe('address_line1')} onBlur={handleBlur}>
              <input type="text" value={profile.address_line1 || ''}
                onChange={e => handleChange('address_line1', e.target.value)}
                placeholder="House no., street, area" />
            </Field>

            <div className="form-row">
              <Field field="city" label="City" error={fe('city')} onBlur={handleBlur}>
                <input type="text" value={profile.city || ''}
                  onChange={e => handleChange('city', e.target.value)}
                  placeholder="e.g. Palai" />
              </Field>
              <Field field="district" label="District" error={fe('district')} onBlur={handleBlur}>
                <select value={profile.district || ''}
                  onChange={e => handleChange('district', e.target.value)}>
                  <option value="">Select district…</option>
                  {KERALA_DISTRICTS.map(d => <option key={d}>{d}</option>)}
                </select>
              </Field>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State</label>
                <input type="text" readOnly value="Kerala" tabIndex="-1" />
              </div>
              <Field field="pin_code" label="PIN Code" error={fe('pin_code')} onBlur={handleBlur}>
                <input type="text" value={profile.pin_code || ''}
                  onChange={e => handleChange('pin_code', e.target.value)}
                  placeholder="e.g. 686575" maxLength={6} />
              </Field>
            </div>

            {/* Education */}
            <div className="section-label">🎓 Education</div>

            <Field field="education_level" label="Highest qualification" error={fe('education_level')} onBlur={handleBlur}>
              <select value={profile.education_level}
                onChange={e => handleChange('education_level', e.target.value)}>
                <option value="">Select…</option>
                {EDUCATION_LEVELS.map(l => <option key={l}>{l}</option>)}
              </select>
            </Field>

            {profile.education_level && (
              <Field field="education_detail" label={EDU_DETAIL_LABEL[profile.education_level]} error={fe('education_detail')} onBlur={handleBlur}>
                <input type="text" value={profile.education_detail || ''}
                  onChange={e => handleChange('education_detail', e.target.value)}
                  placeholder="Enter details…" />
              </Field>
            )}


            {/* Submit */}
            <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}
                style={{ width: 'auto', padding: '12px 36px' }}>
                {saving ? 'Saving…' : isNew ? 'Submit profile →' : 'Save changes →'}
              </button>
              {!valid && submitAttempted && (
                <span style={{ fontSize: '0.83rem', color: 'var(--red)', fontWeight: 600 }}>
                  ⚠ {REQUIRED_FIELDS.length - filledCount} field{REQUIRED_FIELDS.length - filledCount !== 1 ? 's' : ''} still required
                </span>
              )}
              {saved && <span className="saved-badge">✓ Saved</span>}
            </div>
          </form>
        </div>
      </div>

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}

      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: '100%', maxWidth: 400, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--red)' }}>Delete account</h3>
            <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: 'var(--ink)' }}>
              This will <strong>permanently delete all your data</strong> and cannot be undone.
              Type <strong>DELETE</strong> to confirm.
            </p>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <input type="text" value={deleteText}
                onChange={e => setDeleteText(e.target.value)}
                placeholder="Type DELETE to confirm"
                style={{ borderColor: 'var(--red)' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-danger"
                disabled={deleteText !== 'DELETE' || deleteLoading}
                style={{ width: 'auto', padding: '9px 20px' }}
                onClick={async () => {
                  setDeleteLoading(true)
                  const { error } = await supabase.rpc('delete_own_account')
                  if (error) { setDeleteLoading(false); return }
                  await supabase.auth.signOut()
                  window.location.replace('/login')
                }}>
                {deleteLoading ? 'Deleting…' : 'Permanently delete'}
              </button>
              <button className="btn btn-ghost" style={{ padding: '9px 20px' }}
                onClick={() => { setShowDeleteConfirm(false); setDeleteText('') }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
